import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-platform-entry': 'https://readdy.ai/api/search-image?query=A%20modern%20browser%20window%20displaying%20a%20clean%20web%20interface%2C%20warm%20amber%20and%20cream%20color%20scheme%2C%20abstract%20geometric%20background%2C%20soft%20natural%20lighting%2C%20professional%20digital%20aesthetic&width=800&height=450&seq=cat-art-1&orientation=landscape',
  'article-register-login': 'https://readdy.ai/api/search-image?query=A%20clean%20signup%20form%20interface%20on%20a%20warm%20beige%20background%2C%20abstract%20floating%20shapes%20in%20golden%20tones%2C%20soft%20lighting%2C%20modern%20minimalist%20UI%20design%2C%20professional%20look&width=800&height=450&seq=cat-art-2&orientation=landscape',
  'article-platform-overview': 'https://readdy.ai/api/search-image?query=Abstract%20digital%20dashboard%20with%20floating%20data%20cards%20and%20charts%2C%20warm%20golden%20and%20cream%20color%20palette%2C%20soft%20gradient%20background%2C%20clean%20minimalist%20tech%20aesthetic&width=800&height=450&seq=cat-art-3&orientation=landscape',
  'article-quick-start': 'https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20desk%20setup%20with%20an%20open%20notebook%20and%20a%20cup%20of%20coffee%2C%20soft%20cream%20and%20beige%20tones%2C%20gentle%20natural%20lighting%2C%20professional%20and%20inviting%20atmosphere&width=800&height=450&seq=cat-art-4&orientation=landscape',
  'article-security': 'https://readdy.ai/api/search-image?query=A%20shield%20icon%20as%20an%20abstract%20geometric%20shape%20with%20warm%20golden%20glow%2C%20cream%20background%2C%20soft%20lighting%2C%20clean%20minimalist%20tech%20security%20aesthetic%2C%20professional%20look&width=800&height=450&seq=cat-art-5&orientation=landscape',
  'article-faq-entry': 'https://readdy.ai/api/search-image?query=A%20calm%20help%20center%20scene%20with%20soft%20warm%20lighting%2C%20question%20mark%20shapes%20as%20abstract%20geometric%20decorations%2C%20cream%20and%20beige%20tones%2C%20clean%20minimalist%20style&width=800&height=450&seq=cat-art-6&orientation=landscape',
  'article-faq-account': 'https://readdy.ai/api/search-image?query=A%20user%20profile%20icon%20as%20an%20abstract%20geometric%20composition%2C%20warm%20golden%20and%20cream%20tones%2C%20soft%20lighting%2C%20clean%20minimalist%20style%2C%20professional%20look&width=800&height=450&seq=cat-art-7&orientation=landscape',
  'article-faq-usage': 'https://readdy.ai/api/search-image?query=Abstract%20geometric%20connected%20nodes%20representing%20network%20and%20functionality%2C%20warm%20amber%20and%20cream%20tones%2C%20soft%20gradient%20background%2C%20clean%20minimalist%20tech%20aesthetic&width=800&height=450&seq=cat-art-8&orientation=landscape',
  'article-terms': 'https://readdy.ai/api/search-image?query=A%20clean%20document%20icon%20as%20abstract%20geometric%20shapes%2C%20warm%20beige%20and%20cream%20tones%2C%20soft%20lighting%2C%20minimalist%20legal%20aesthetic%2C%20professional%20look&width=800&height=450&seq=cat-art-9&orientation=landscape',
  'article-privacy': 'https://readdy.ai/api/search-image?query=A%20lock%20icon%20as%20abstract%20geometric%20composition%20with%20warm%20golden%20accents%2C%20cream%20background%2C%20soft%20lighting%2C%20clean%20minimalist%20aesthetic%2C%20professional%20look&width=800&height=450&seq=cat-art-10&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = getArticlesByCategory(slug || '');
  const otherCategories = categories.filter((c) => c.slug !== slug);
  const canonicalPath = `/category/${slug}`;

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${category.name} - http //www.17c/ 官方入口与使用指南`,
          description: category.description,
          url: `https://www.17c.com${canonicalPath}`,
        }}
      />

      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="pt-24 pb-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 pb-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
              </div>
            </div>
            <p className="text-sm text-foreground-600 max-w-2xl mb-10 leading-relaxed">
              {category.description}
            </p>

            <div className="flex gap-2 mb-10 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={articleImages[article.imageSeq] || articleImages['article-platform-entry']}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-background-50/85 backdrop-blur-sm text-primary-700">
                          {category.name}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h2 className="font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 text-base group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.publishDate}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>

        {otherCategories.length > 0 && (
          <section className="w-full px-4 md:px-6 py-16 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                浏览其他分类
              </h2>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                {otherCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 px-6 py-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-foreground-900 text-sm">{cat.name}</div>
                      <div className="text-xs text-foreground-500 mt-0.5">查看全部</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}